// code for plate number detection and masked
import { useEffect, useRef, useState } from "react";
import { createWorker } from "tesseract.js";
import CustomButton from "../Form/CustomButton";
import CustomSelect2 from "../Form/CustomSelect2";

// Plate OCR is temporarily disabled. Uncomment the next line and remove the
// false fallback to restore it.
// const ENABLE_PLATE_DETECTION = true;
const ENABLE_PLATE_DETECTION = false;

// SG plate formats (simple) e.g. "SBS1234A", "SK 1234 A"
const SG_PLATE_STRICT = /^[A-Z]{1,3}\d{1,4}[A-Z]$/;
// const SG_PLATE_STRICT = /[A-Z]{1,3}\d{1,4}[A-Z]/;

// ---- Helpers ----
function clamp(v, min, max) {
    return Math.max(min, Math.min(max, v));
}

function normalizeRect(r) {
    if (!r) return r;
    const x = r.w < 0 ? r.x + r.w : r.x;
    const y = r.h < 0 ? r.y + r.h : r.y;
    const w = Math.abs(r.w);
    const h = Math.abs(r.h);
    return { x, y, w, h };
}

function rectContains(r, x, y) {
    return x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
}

function toIntRect(r) {
    return {
        x: Math.max(0, Math.floor(r.x)),
        y: Math.max(0, Math.floor(r.y)),
        w: Math.max(1, Math.floor(r.w)),
        h: Math.max(1, Math.floor(r.h)),
    };
}

// Remove whitespace/symbols; keep only A-Z0-9 and join
function cleanForPlate(s) {
    return (s || "")
        .toUpperCase()
        .replace(/[—–-]/g, "")
        .replace(/[^A-Z0-9]/g, "");
}

// Find plate from full OCR text (no bbox)
function findPlateFromText(text) {
    if (!text) return null;

    const parts = text.split(/[\s\n]+/);

    for (let part of parts) {
        const cleaned = part.toUpperCase().replace(/[^A-Z0-9]/g, "");

        if (SG_PLATE_STRICT.test(cleaned)) {
            return cleaned;
        }
    }
    return null;
}

// Default box when a plate is detected (bottom-center)
function getDefaultPlateRect(bitmap) {
    const W = bitmap.width;
    const H = bitmap.height;

    // Tweak these ratios if you want
    const boxW = Math.round(W * 0.45);
    const boxH = Math.round(H * 0.14);
    const x = Math.round((W - boxW) / 2);
    const y = Math.round(H * 0.72);

    const rx = clamp(x, 0, W - boxW);
    const ry = clamp(y, 0, H - boxH);

    return { x: rx, y: ry, w: boxW, h: boxH };
}

export default function AppMaskedPlateNumber({ imageData, setImageData }) {
    const canvasRef = useRef(null);

    const [imgBitmap, setImgBitmap] = useState(null);
    const [status, setStatus] = useState("");
    const [rect, setRect] = useState(null); // {x,y,w,h} in canvas coords
    const [maskMode, setMaskMode] = useState("pixelate"); // "blur" | "pixelate"

    // For Reset
    const [originalBitmap, setOriginalBitmap] = useState(null);
    const [originalRect, setOriginalRect] = useState(null);

    // OCR result
    const [isPlateDetected, setIsPlateDetected] = useState(false);
    const [detectedPlate, setDetectedPlate] = useState("");

    // action: move/resize/draw
    const [action, setAction] = useState(null);

    const HANDLE_SIZE = 10;
    const HANDLE_HIT = 14;

    const hitTestHandle = (r, px, py) => {
        if (!r) return null;
        const corners = {
            nw: { x: r.x, y: r.y },
            ne: { x: r.x + r.w, y: r.y },
            sw: { x: r.x, y: r.y + r.h },
            se: { x: r.x + r.w, y: r.y + r.h },
        };
        for (const key of Object.keys(corners)) {
            const c = corners[key];
            if (Math.abs(px - c.x) <= HANDLE_HIT && Math.abs(py - c.y) <= HANDLE_HIT) {
                return key;
            }
        }
        return null;
    };

    const getCanvasPoint = (e) => {
        const c = canvasRef.current;
        const r = c.getBoundingClientRect();
        const scaleX = c.width / r.width;
        const scaleY = c.height / r.height;
        return {
            x: (e.clientX - r.left) * scaleX,
            y: (e.clientY - r.top) * scaleY,
        };
    };

    // Draw image + rect overlay
    useEffect(() => {
        if (!imgBitmap || !canvasRef.current) return;
        const c = canvasRef.current;
        const ctx = c.getContext("2d");

        c.width = imgBitmap.width;
        c.height = imgBitmap.height;

        ctx.clearRect(0, 0, c.width, c.height);
        ctx.drawImage(imgBitmap, 0, 0);

        if (rect) {
            const r = normalizeRect(rect);
            ctx.save();

            ctx.strokeStyle = "#ff3b30";
            ctx.lineWidth = 3;
            ctx.setLineDash([8, 6]);
            ctx.strokeRect(r.x, r.y, r.w, r.h);

            ctx.setLineDash([]);
            ctx.fillStyle = "#ff3b30";
            const hs = HANDLE_SIZE;
            ctx.fillRect(r.x - hs / 2, r.y - hs / 2, hs, hs);
            ctx.fillRect(r.x + r.w - hs / 2, r.y - hs / 2, hs, hs);
            ctx.fillRect(r.x - hs / 2, r.y + r.h - hs / 2, hs, hs);
            ctx.fillRect(r.x + r.w - hs / 2, r.y + r.h - hs / 2, hs, hs);

            ctx.restore();
        }
    }, [imgBitmap, rect]);

    useEffect(() => {
        if (imageData) {
            const img = new Image();
            img.src = imageData; // Set the base64 string as the image source

            img.onload = async () => {
                try {
                    const bitmap = await createImageBitmap(img); // Convert the image to a bitmap
                    setImgBitmap(bitmap); // Set the bitmap as the main image

                    // Optionally, set the default plate rectangle
                    const defaultRect = getDefaultPlateRect(bitmap);
                    setRect(defaultRect);
                    setOriginalBitmap(bitmap);
                    setOriginalRect(defaultRect);
                    setStatus("Image loaded from parent data.");

                    if (!ENABLE_PLATE_DETECTION) {
                        setIsPlateDetected(false);
                        setDetectedPlate("");
                        setStatus("Adjust the box manually to mask the number plate.");
                        return;
                    }

                    // Start detecting the plate number immediately
                    setStatus("Detecting plate (OCR)…");
                    const worker = await createWorker("eng", 1, {
                        workerPath: '/tesseract/worker.min.js',
                        corePath: '/tesseract/tesseract-core-simd-lstm.wasm.js',
                        langPath: '/tesseract/lang-data',
                        logger: (m) => {
                            if (m.status) setStatus(`${m.status} ${Math.round((m.progress || 0) * 100)}%`);
                        },
                    });

                    try {
                        await worker.setParameters({
                            tessedit_pageseg_mode: "6",
                            tessedit_char_whitelist: "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"
                        });
                    } catch { }

                    const canvas = document.createElement("canvas");
                    canvas.width = bitmap.width;
                    canvas.height = bitmap.height;
                    const ctx = canvas.getContext("2d");
                    ctx.drawImage(bitmap, 0, 0);

                    const { data } = await worker.recognize(canvas);

                    const plate = findPlateFromText(data.text);
                    if (plate) {
                        setIsPlateDetected(true);
                        setDetectedPlate(plate);
                        setStatus(`Plate detected ✅ (${plate}). Default box is ready (you can adjust).`);
                    } else {
                        setIsPlateDetected(false);
                        setDetectedPlate("");
                        setStatus("No plate detected ❌. Draw/adjust the box manually.");
                    }

                    await worker.terminate();
                } catch (error) {
                    console.error("Failed to process imageData:", error);
                    setStatus("Failed to load image from parent data.");
                }
            };

            img.onerror = () => {
                console.error("Invalid base64 image data.");
                setStatus("Invalid image data provided.");
            };
        }
    }, [imageData])

    const updateCursor = (e) => {
        if (!canvasRef.current || !imgBitmap) return;
        if (!rect) {
            canvasRef.current.style.cursor = "crosshair";
            return;
        }

        const r = normalizeRect(rect);
        const { x, y } = getCanvasPoint(e);
        const handle = hitTestHandle(r, x, y);

        if (handle === "nw" || handle === "se") canvasRef.current.style.cursor = "nwse-resize";
        else if (handle === "ne" || handle === "sw") canvasRef.current.style.cursor = "nesw-resize";
        else if (rectContains(r, x, y)) canvasRef.current.style.cursor = "move";
        else canvasRef.current.style.cursor = "crosshair";
    };

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setStatus("Loading image…");
        setRect(null);
        setAction(null);
        setIsPlateDetected(false);
        setDetectedPlate("");

        const bitmap = await createImageBitmap(file);
        setImgBitmap(bitmap);

        setOriginalBitmap(bitmap);
        setOriginalRect(null);

        if (!ENABLE_PLATE_DETECTION) {
            const defaultRect = getDefaultPlateRect(bitmap);
            setRect(defaultRect);
            setOriginalRect(defaultRect);
            setStatus("Adjust the box manually to mask the number plate.");
            return;
        }

        let url;
        let worker = null;

        try {
            setStatus("Detecting plate (OCR)…");
            url = URL.createObjectURL(file);

            worker = await createWorker("eng", 1, {
                workerPath: '/tesseract/worker.min.js',
                corePath: '/tesseract/tesseract-core-simd-lstm.wasm.js',
                langPath: '/tesseract/lang-data',
                logger: (m) => {
                    if (m.status) setStatus(`${m.status} ${Math.round((m.progress || 0) * 100)}%`);
                },
            });

            try {
                await worker.setParameters({
                    tessedit_pageseg_mode: "6",
                    tessedit_char_whitelist: "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"
                });
            } catch { }

            // Only need full text; no blocks/bboxes
            const { data } = await worker.recognize(url);

            console.log("[OCR] full text:", data.text);

            const plate = findPlateFromText(data.text);

            if (plate) {
                setIsPlateDetected(true);
                setDetectedPlate(plate);

                // Key change: use a default box, not OCR character locations
                const defaultRect = getDefaultPlateRect(bitmap);
                setRect(defaultRect);
                setOriginalRect(defaultRect);

                setStatus(`Plate detected ✅ (${plate}). Default box is ready (you can adjust).`);
            } else {
                setIsPlateDetected(false);
                setDetectedPlate("");
                setRect(null);
                setOriginalRect(null);
                setStatus("No plate detected ❌. Draw/adjust the box manually.");
            }
        } catch (err) {
            console.error(err);
            setIsPlateDetected(false);
            setDetectedPlate("");
            setRect(null);
            setOriginalRect(null);
            setStatus("OCR failed ❌. Draw/adjust the box manually.");
        } finally {
            if (worker) {
                try {
                    await worker.terminate();
                } catch { }
            }
            if (url) URL.revokeObjectURL(url);
        }
    };

    const onMouseDown = (e) => {
        if (!canvasRef.current || !imgBitmap) return;

        const { x, y } = getCanvasPoint(e);

        if (!rect) {
            setRect({ x, y, w: 1, h: 1 });
            setAction({ type: "draw", startX: x, startY: y });
            return;
        }

        const r = normalizeRect(rect);
        const handle = hitTestHandle(r, x, y);

        if (handle) {
            setAction({ type: "resize", handle, startX: x, startY: y, startRect: r });
            return;
        }

        if (rectContains(r, x, y)) {
            setAction({ type: "move", dx: x - r.x, dy: y - r.y });
            return;
        }

        setRect({ x, y, w: 1, h: 1 });
        setAction({ type: "draw", startX: x, startY: y });
    };

    const onMouseMove = (e) => {
        if (!canvasRef.current || !imgBitmap) return;

        updateCursor(e);
        if (!action) return;

        const c = canvasRef.current;
        const { x, y } = getCanvasPoint(e);

        if (action.type === "move") {
            const r = normalizeRect(rect);
            const nx = clamp(x - action.dx, 0, c.width - r.w);
            const ny = clamp(y - action.dy, 0, c.height - r.h);
            setRect({ ...r, x: nx, y: ny });
            return;
        }

        if (action.type === "draw") {
            setRect({ x: action.startX, y: action.startY, w: x - action.startX, h: y - action.startY });
            return;
        }

        if (action.type === "resize") {
            const { handle, startRect, startX, startY } = action;
            const dx = x - startX;
            const dy = y - startY;

            let { x: rx, y: ry, w: rw, h: rh } = startRect;

            if (handle === "se") {
                rw = startRect.w + dx;
                rh = startRect.h + dy;
            } else if (handle === "sw") {
                rx = startRect.x + dx;
                rw = startRect.w - dx;
                rh = startRect.h + dy;
            } else if (handle === "ne") {
                ry = startRect.y + dy;
                rw = startRect.w + dx;
                rh = startRect.h - dy;
            } else if (handle === "nw") {
                rx = startRect.x + dx;
                ry = startRect.y + dy;
                rw = startRect.w - dx;
                rh = startRect.h - dy;
            }

            let nr = normalizeRect({ x: rx, y: ry, w: rw, h: rh });

            const MIN_W = 30;
            const MIN_H = 20;
            nr.w = Math.max(MIN_W, nr.w);
            nr.h = Math.max(MIN_H, nr.h);

            nr.x = clamp(nr.x, 0, c.width - nr.w);
            nr.y = clamp(nr.y, 0, c.height - nr.h);

            setRect(nr);
        }
    };

    const onMouseUp = () => {
        if (!rect) {
            setAction(null);
            return;
        }

        const c = canvasRef.current;
        let nr = normalizeRect(rect);

        if (c) {
            nr.x = clamp(nr.x, 0, c.width - nr.w);
            nr.y = clamp(nr.y, 0, c.height - nr.h);
            nr.w = clamp(nr.w, 1, c.width - nr.x);
            nr.h = clamp(nr.h, 1, c.height - nr.y);
        }

        setRect(nr);
        setAction(null);
    };

    const onTouchStart = (e) => {
        if (!e.touches?.[0]) return;
        e.preventDefault();
        onMouseDown(e.touches[0]);
    };

    const onTouchMove = (e) => {
        if (!e.touches?.[0]) return;
        e.preventDefault();
        onMouseMove(e.touches[0]);
    };

    const onTouchEnd = (e) => {
        e.preventDefault();
        onMouseUp();
    };

    const applyMaskAndReplace = async () => {
        if (!imgBitmap || !rect) return;

        const r = toIntRect(normalizeRect(rect));

        const c = document.createElement("canvas");
        c.width = imgBitmap.width;
        c.height = imgBitmap.height;
        const ctx = c.getContext("2d");
        ctx.drawImage(imgBitmap, 0, 0);

        if (maskMode === "blur") {
            const region = ctx.getImageData(r.x, r.y, r.w, r.h);

            const tmp = document.createElement("canvas");
            tmp.width = r.w;
            tmp.height = r.h;

            const tctx = tmp.getContext("2d");
            tctx.putImageData(region, 0, 0);

            ctx.save();

            // Apply blur multiple times
            ctx.filter = "blur(30px)";
            ctx.drawImage(tmp, r.x, r.y);

            ctx.filter = "blur(30px)";
            ctx.drawImage(tmp, r.x, r.y);

            ctx.filter = "blur(30px)";
            ctx.drawImage(tmp, r.x, r.y);

            ctx.restore();
        } else {
            const scale = 0.03;
            const pw = Math.max(1, Math.floor(r.w * scale));
            const ph = Math.max(1, Math.floor(r.h * scale));

            const tmp = document.createElement("canvas");
            tmp.width = pw;
            tmp.height = ph;
            const tctx = tmp.getContext("2d");

            tctx.imageSmoothingEnabled = true;
            tctx.drawImage(c, r.x, r.y, r.w, r.h, 0, 0, pw, ph);

            ctx.save();
            ctx.imageSmoothingEnabled = false;
            ctx.drawImage(tmp, 0, 0, pw, ph, r.x, r.y, r.w, r.h);
            ctx.restore();
        }

        const blob = await new Promise((resolve) => c.toBlob(resolve, "image/png"));
        if (!blob) {
            setStatus("Failed to replace image.");
            return;
        }
        const processedBitmap = await createImageBitmap(blob);
        console.log("hello", processedBitmap)
        setImgBitmap(processedBitmap);

        // convert to base64 and send back
        const reader = new FileReader();
        reader.onloadend = () => {
            setImageData(reader.result);
        };
        reader.readAsDataURL(blob);

        setRect(null);

        setStatus("Image replaced with a masked version. Click Reset to restore the original.");
    };

    const handleReset = () => {
        if (!originalBitmap) return;
        setImgBitmap(originalBitmap);
        setRect(originalRect || null);
        setAction(null);
        //convert to base64 and send back
        const c = document.createElement("canvas");
        c.width = originalBitmap.width;
        c.height = originalBitmap.height;
        const ctx = c.getContext("2d");
        ctx.drawImage(originalBitmap, 0, 0);

        c.toBlob((blob) => {
            if (!blob) {
                setStatus("Failed to reset image.");
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setImageData(reader.result);
            };
            reader.readAsDataURL(blob);
        }, "image/png");

        setStatus("Restored the original image.");
    };

    // Upload enabled only if plate detected
    const uploadDisabled = !imgBitmap || (ENABLE_PLATE_DETECTION && !isPlateDetected);

    return (
        <div style={{ maxWidth: '100%', padding: 16 }}>
            <div style={{ position: "sticky", overflow: "hidden", top: 0, left: 0, right: 0, width: "100%", boxSizing: "border-box", backgroundColor: "#fff", zIndex: 10, paddingBottom: 8 }}>
                <div style={{ display: "flex", gap: 5, alignItems: "center", flexWrap: "wrap" }}>
                    {
                        !imageData &&
                        <input type="file" accept="image/*" onChange={onFile} />
                    }
                    <label>
                        Mask:&nbsp;
                        <CustomSelect2
                            values={maskMode}
                            onChange={(e) => setMaskMode(e)}
                            data={[{ label: 'Blur', value: 'blur' }, { label: 'Pixelate', value: 'pixelate' }]}
                        />
                    </label>

                    {
                        !imageData &&
                        <button disabled={uploadDisabled}>Upload</button>
                    }
                    <CustomButton
                        title={'Replace'}
                        press={applyMaskAndReplace}
                        disabled={!imgBitmap || !rect}
                        backgroundColor={''}
                    />
                    <CustomButton
                        title={'Reset'}
                        press={handleReset}
                        disabled={!originalBitmap}
                        backgroundColor={'#F9F9F9'}
                        borderStyle={'1px solid #F9F9F9'}
                        textColor={'#808588'}
                    />
                </div>

                <div style={{ marginTop: 5, color: "#666" }}>
                    {status}
                    {detectedPlate ? <div style={{ marginTop: 4, color: "#888" }}>Detected plate: {detectedPlate}</div> : null}
                </div>
            </div>

            <div style={{ marginTop: 12, border: "1px solid #eee", borderRadius: 8, overflow: "hidden" }}>
                <canvas
                    ref={canvasRef}
                    style={{ width: "100%", height: "auto", display: "block", touchAction: "none" }}
                    onMouseDown={onMouseDown}
                    onMouseMove={onMouseMove}
                    onMouseUp={onMouseUp}
                    onMouseLeave={onMouseUp}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                />
            </div>
        </div>
    );
}
